import { lazy, Suspense } from "react";
import { useUTMCapture } from "@/hooks/use-utm";
import { useGlobalClickTracking } from "@/hooks/use-click-tracking";
import { useCanonical } from "@/hooks/use-canonical";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import DealsPopup from "@/components/DealsPopup";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

const Products = lazy(() => import("./pages/Products"));
const Teardrop = lazy(() => import("./pages/products/Teardrop"));
const Selective = lazy(() => import("./pages/products/Selective"));
const DriveIn = lazy(() => import("./pages/products/DriveIn"));
const PushBack = lazy(() => import("./pages/products/PushBack"));
const Cantilever = lazy(() => import("./pages/products/Cantilever"));
const Structural = lazy(() => import("./pages/products/Structural"));
const Tubular = lazy(() => import("./pages/products/Tubular"));
const DoubleDeep = lazy(() => import("./pages/products/DoubleDeep"));
const DriveThrough = lazy(() => import("./pages/products/DriveThrough"));
const PalletFlow = lazy(() => import("./pages/products/PalletFlow"));
const VNA = lazy(() => import("./pages/products/VNA"));
const Mobile = lazy(() => import("./pages/products/Mobile"));
const Shuttle = lazy(() => import("./pages/products/Shuttle"));
const ASRS = lazy(() => import("./pages/products/ASRS"));
const StackRacks = lazy(() => import("./pages/products/StackRacks"));
const PostPallets = lazy(() => import("./pages/products/PostPallets"));
const CladRack = lazy(() => import("./pages/products/CladRack"));
const MultiDeepASRS = lazy(() => import("./pages/products/MultiDeepASRS"));
const HybridBeamRail = lazy(() => import("./pages/products/HybridBeamRail"));
const Services = lazy(() => import("./pages/Services"));
const NewRacking = lazy(() => import("./pages/services/NewRacking"));
const UsedRacking = lazy(() => import("./pages/services/UsedRacking"));
const SellRacking = lazy(() => import("./pages/services/SellRacking"));
const Installation = lazy(() => import("./pages/services/Installation"));
const Inspection = lazy(() => import("./pages/services/Inspection"));
const Locations = lazy(() => import("./pages/Locations"));
const Utah = lazy(() => import("./pages/locations/Utah"));
const NorthCarolina = lazy(() => import("./pages/locations/NorthCarolina"));
const Ontario = lazy(() => import("./pages/locations/Ontario"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Quote = lazy(() => import("./pages/Quote"));
const Resources = lazy(() => import("./pages/Resources"));
const SafetyStandards = lazy(() => import("./pages/resources/SafetyStandards"));
const OSHAGuidelines = lazy(() => import("./pages/resources/OSHAGuidelines"));
const InspectionGuide = lazy(() => import("./pages/resources/InspectionGuide"));
const LayoutTips = lazy(() => import("./pages/resources/LayoutTips"));
const RackCompatibility = lazy(() => import("./pages/resources/RackCompatibility"));
const CostEstimator = lazy(() => import("./pages/tools/CostEstimator"));
const WarehouseDraw = lazy(() => import("./pages/tools/WarehouseDraw"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const IdentifyRack = lazy(() => import("./pages/IdentifyRack"));
const Booking = lazy(() => import("./pages/Booking"));
const DriveInVsPushBack = lazy(() => import("./pages/comparisons/DriveInVsPushBack"));
const SelectiveVsDoubleDeep = lazy(() => import("./pages/comparisons/SelectiveVsDoubleDeep"));
const PalletFlowVsPushBack = lazy(() => import("./pages/comparisons/PalletFlowVsPushBack"));
const DriveInVsShuttle = lazy(() => import("./pages/comparisons/DriveInVsShuttle"));
const StructuralVsRollFormed = lazy(() => import("./pages/comparisons/StructuralVsRollFormed"));
const MobileVsDriveIn = lazy(() => import("./pages/comparisons/MobileVsDriveIn"));
const queryClient = new QueryClient();

const AppRoutes = () => {
  useUTMCapture();
  useCanonical();
  useGlobalClickTracking();
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/products" element={<Suspense fallback={<div />}><Products /></Suspense>} />
      <Route path="/products/teardrop" element={<Suspense fallback={<div />}><Teardrop /></Suspense>} />
      <Route path="/products/selective" element={<Suspense fallback={<div />}><Selective /></Suspense>} />
      <Route path="/products/drive-in" element={<Suspense fallback={<div />}><DriveIn /></Suspense>} />
      <Route path="/products/push-back" element={<Suspense fallback={<div />}><PushBack /></Suspense>} />
      <Route path="/products/cantilever" element={<Suspense fallback={<div />}><Cantilever /></Suspense>} />
      <Route path="/products/structural" element={<Suspense fallback={<div />}><Structural /></Suspense>} />
      <Route path="/products/tubular" element={<Suspense fallback={<div />}><Tubular /></Suspense>} />
      <Route path="/products/double-deep" element={<Suspense fallback={<div />}><DoubleDeep /></Suspense>} />
      <Route path="/products/drive-through" element={<Suspense fallback={<div />}><DriveThrough /></Suspense>} />
      <Route path="/products/pallet-flow" element={<Suspense fallback={<div />}><PalletFlow /></Suspense>} />
      <Route path="/products/vna" element={<Suspense fallback={<div />}><VNA /></Suspense>} />
      <Route path="/products/mobile" element={<Suspense fallback={<div />}><Mobile /></Suspense>} />
      <Route path="/products/shuttle" element={<Suspense fallback={<div />}><Shuttle /></Suspense>} />
      <Route path="/products/asrs" element={<Suspense fallback={<div />}><ASRS /></Suspense>} />
      <Route path="/products/stack-racks" element={<Suspense fallback={<div />}><StackRacks /></Suspense>} />
      <Route path="/products/post-pallets" element={<Suspense fallback={<div />}><PostPallets /></Suspense>} />
      <Route path="/products/clad-rack" element={<Suspense fallback={<div />}><CladRack /></Suspense>} />
      <Route path="/products/multi-deep-asrs" element={<Suspense fallback={<div />}><MultiDeepASRS /></Suspense>} />
      <Route path="/products/hybrid-beam-rail" element={<Suspense fallback={<div />}><HybridBeamRail /></Suspense>} />
      <Route path="/compare/drive-in-vs-push-back" element={<Suspense fallback={<div />}><DriveInVsPushBack /></Suspense>} />
      <Route path="/compare/selective-vs-double-deep" element={<Suspense fallback={<div />}><SelectiveVsDoubleDeep /></Suspense>} />
      <Route path="/compare/pallet-flow-vs-push-back" element={<Suspense fallback={<div />}><PalletFlowVsPushBack /></Suspense>} />
      <Route path="/compare/drive-in-vs-shuttle" element={<Suspense fallback={<div />}><DriveInVsShuttle /></Suspense>} />
      <Route path="/compare/structural-vs-roll-formed" element={<Suspense fallback={<div />}><StructuralVsRollFormed /></Suspense>} />
      <Route path="/compare/mobile-vs-drive-in" element={<Suspense fallback={<div />}><MobileVsDriveIn /></Suspense>} />
      <Route path="/services" element={<Suspense fallback={<div />}><Services /></Suspense>} />
      <Route path="/services/new" element={<Suspense fallback={<div />}><NewRacking /></Suspense>} />
      <Route path="/services/used" element={<Suspense fallback={<div />}><UsedRacking /></Suspense>} />
      <Route path="/services/sell" element={<Suspense fallback={<div />}><SellRacking /></Suspense>} />
      <Route path="/services/installation" element={<Suspense fallback={<div />}><Installation /></Suspense>} />
      <Route path="/services/inspection" element={<Suspense fallback={<div />}><Inspection /></Suspense>} />
      <Route path="/locations" element={<Suspense fallback={<div />}><Locations /></Suspense>} />
      <Route path="/locations/utah" element={<Suspense fallback={<div />}><Utah /></Suspense>} />
      <Route path="/locations/north-carolina" element={<Suspense fallback={<div />}><NorthCarolina /></Suspense>} />
      <Route path="/locations/ontario" element={<Suspense fallback={<div />}><Ontario /></Suspense>} />
      <Route path="/about" element={<Suspense fallback={<div />}><About /></Suspense>} />
      <Route path="/contact" element={<Suspense fallback={<div />}><Contact /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<div />}><Privacy /></Suspense>} />
      <Route path="/terms" element={<Suspense fallback={<div />}><Terms /></Suspense>} />
      <Route path="/quote" element={<Suspense fallback={<div />}><Quote /></Suspense>} />
      <Route path="/resources" element={<Suspense fallback={<div />}><Resources /></Suspense>} />
      <Route path="/resources/safety" element={<Suspense fallback={<div />}><SafetyStandards /></Suspense>} />
      <Route path="/resources/osha" element={<Suspense fallback={<div />}><OSHAGuidelines /></Suspense>} />
      <Route path="/resources/inspection-guide" element={<Suspense fallback={<div />}><InspectionGuide /></Suspense>} />
      <Route path="/resources/layout" element={<Suspense fallback={<div />}><LayoutTips /></Suspense>} />
      <Route path="/resources/rack-compatibility" element={<Suspense fallback={<div />}><RackCompatibility /></Suspense>} />
      <Route path="/tools/estimator" element={<Suspense fallback={<div />}><CostEstimator /></Suspense>} />
      <Route path="/tools/warehouse-draw" element={<Suspense fallback={<div />}><WarehouseDraw /></Suspense>} />
      <Route path="/how-it-works" element={<Suspense fallback={<div />}><HowItWorks /></Suspense>} />
      <Route path="/identify-rack" element={<Suspense fallback={<div />}><IdentifyRack /></Suspense>} />
      <Route path="/booking" element={<Suspense fallback={<div />}><Booking /></Suspense>} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => {
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <DealsPopup />
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
